/**
 * FullscreenPanel — wraps any visualization panel and adds an in-app
 * maximize button (top-right). When expanded, the child fills the entire
 * browser viewport (position: fixed, z-index: 60) on a white backdrop.
 * Press Escape or click the collapse button to return to inline layout.
 *
 * Usage:
 *   <FullscreenPanel title="3D block model" testid="block-model-viewer">
 *     <MyViewer />
 *   </FullscreenPanel>
 */
import { useEffect, useState, useCallback } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

export default function FullscreenPanel({
  children,
  title,
  className = "",
  testid,
  fullscreenClassName = "",
}) {
  const [full, setFull] = useState(false);

  const toggle = useCallback(() => setFull((v) => !v), []);
  const close = useCallback(() => setFull(false), []);

  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while maximised
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [full, close]);

  const wrapperCls = full
    ? `fixed inset-0 z-[60] bg-white flex flex-col ${fullscreenClassName}`
    : `relative ${className}`;

  return (
    <div className={wrapperCls} data-testid={testid}>
      {full && title && (
        <div className="h-11 px-4 border-b border-neutral-200 flex items-center justify-between bg-neutral-50 shrink-0">
          <div className="text-[13px] font-medium text-neutral-900 tracking-tight">
            {title}
          </div>
          <div className="text-[10.5px] text-neutral-500 uppercase tracking-widest">
            Press <kbd className="px-1.5 py-0.5 border border-neutral-300 rounded text-[10px] font-mono">Esc</kbd> to close
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={toggle}
        data-testid={testid ? `${testid}-fullscreen-toggle` : "fullscreen-toggle"}
        title={full ? "Collapse (Esc)" : "Maximize"}
        aria-label={full ? "Collapse panel" : "Maximize panel"}
        className={`absolute ${full ? "top-2 right-3" : "top-2 right-2"} z-10 h-8 w-8 grid place-items-center rounded-md border border-neutral-200 bg-white/95 backdrop-blur text-neutral-700 hover:text-[#4CAF50] hover:border-[#4CAF50] shadow-sm transition`}
      >
        {full ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
      <div className={full ? "flex-1 min-h-0 overflow-auto" : ""}>
        {children}
      </div>
    </div>
  );
}
